import Vue from 'vue';
import Vuex from 'vuex';

import { Plugins } from '@capacitor/core';
const { Storage } = Plugins;

import subscriptions from './modules/subscriptions';
import articles from './modules/articles';
import documents from './modules/documents';
import users from './modules/users';
import contacts from './modules/contacts';
import fixtures from './modules/fixtures';
import messaging from './modules/messaging';
import stamps from './modules/stamps';
import annotations from './modules/annotations';
import search from './modules/search';
import storage from './modules/storage';

Vue.use(Vuex);

const store = new Vuex.Store({
    modules: {
        subscriptions,
        articles,
        documents,
        users,
        contacts,
        fixtures,
        messaging,
        annotations,
        stamps,
        search,
        storage
    },

    state: {
        eula: false,
        authorization: '',
        online_status: true,
        desktop: false,
        height: 0,
        width: 0,
        menu_items: [],
        menu_details: {
            title: '',
            slug: '',
        },
        activeMenu: true,
        manual_back_button: true,
        current_page: null,
        previous_backURL: null,
        backURL: null,
        animation: 'next',
        syncing: false,
        lastSync: '',
        fcmToken: false,
        lastBuildVersion: false,
        currentBuild: 73
    },

    mutations: {
        setEula(state, data) {
            Storage.set({
                key: 'eula',
                value: data
            });

            state.eula = data;
        },

        setSync(state, data) {
            state.syncing = data;
        },

        setFCMToken(state, data) {
            state.fcmToken = data;
        },

        setLastSync(state, data) {
            state.lastSync = data;
        },

        setAuthorization(state, data) {
            Storage.set({
                key: 'authorization',
                value: data
            });
            state.authorization = data;
        },

        setLastBuild(state, data) {
            Storage.set({
                key: 'last_build',
                value: data
            });
            state.lastBuildVersion = data;
        },

        setOnlineStatus(state, data) {
            state.online_status = data;
        },

        setDesktop(state, data) {
            Vue.set(state, 'desktop', data);
        },

        setHeight(state, data) {
            Vue.set(state, 'height', data);
        },

        setWidth(state, data) {
            Vue.set(state, 'width', data);
        },

        setMenuItems(state, data) {
            state.menu_items = data;
        },

        setMenuDetails(state, data) {
            state.menu_details = data;
        },

        setPreviousPage(state, data) {
            state.previous_page = data;
        },

        setBackURL(state, data) {
            state.previous_backURL = state.backURL;
            state.backURL = data;
        },

        setAnimation(state, data) {
            state.animation = data;
        },

        setActiveMenu(state, data) {
            state.activeMenu = data;
        }
    },

    actions: {
        checkEula({ state, dispatch }) {
            return new Promise((resolve, reject) => {
                Storage.get({ key: 'eula' }).then((response) => {
                    if (response.value) {
                        state.eula = response.value;
                        resolve();
                    } else {
                        reject();
                    }
                });
            });
        },

        checkAuthorization({ state, dispatch }) {
            return new Promise((resolve, reject) => {
                Storage.get({ key: 'authorization' }).then((response) => {
                    if (response.value) {
                        state.authorization = response.value;
                        dispatch('updateData');
                        resolve();
                    } else {
                        reject();
                    }
                });
            });
        },

        clearData({ state, commit, dispatch }) {
            return new Promise((resolve) => {
                state.authorization = '';

                dispatch('clearSubscriptionsData');
                dispatch('clearArticlesData');
                dispatch('clearAnnotationsList');
                dispatch('clearSharedAnnotations');
                dispatch('clearDocumentsData');
                dispatch('clearUserData');
                dispatch('clearContactsData');
                dispatch('clearFixturesData');
                dispatch('clearStamps');
                
                Storage.clear().then(() => {
                    if (state.eula) {
                        commit('setEula', state.eula);
                    }
                    
                    resolve();
                });
            });
        },

        updateData({ dispatch }) {
            dispatch('getSubscriptionData');
            dispatch('getArticlesData');
            dispatch('getDocumentsData');
            dispatch('getUserData');
            dispatch('getContactsData');
            dispatch('getContactTypesData');
            dispatch('getFixturesData');
            dispatch('getFixtureTypesData');
            dispatch('getAnnotationsList');
            dispatch('getSharedAnnotations');
            dispatch('getStampsList');
            dispatch('registerPushToken');
            dispatch('checkSubscriptionCache');
            dispatch('checkDocumentsCache');
        },

        handleOnlineStatusChange({ dispatch, commit, rootState }) {
            // wait a second, and then fire off queues
            setTimeout(() => {
                if (rootState.authorization) {
                    dispatch('processAnnotationQueue');
                    dispatch('fetchDocuments');
                    dispatch('fetchStamps');
                    dispatch('checkSubscriptionCache');
                    dispatch('checkDocumentsCache');
                }
            });
        },

        async fetchInitialData({ dispatch }) {
            await dispatch('fetchAllStamps');
            let fetchArticles = dispatch('fetchArticles'),
                fetchDocuments = dispatch('fetchDocuments'),
                fetchStamps = dispatch('fetchStamps'),
                fetchAnnotations = dispatch('fetchAnnotations'),
                fetchSharedAnnotations = dispatch('fetchSharedAnnotations'),
                fetchUser = dispatch('fetchUser'),
                fetchBuildingProfile = dispatch('fetchBuildingProfile'),
                fetchContacts = dispatch('fetchContacts'),
                fetchContactTypes = dispatch('fetchContactTypes'),
                fetchFixtures = dispatch('fetchFixtures'),
                fetchFixtureTypes = dispatch('fetchFixtureTypes');
            return Promise.all([fetchArticles, fetchDocuments, fetchAnnotations, fetchSharedAnnotations, fetchUser, fetchContacts, fetchContactTypes, fetchBuildingProfile, fetchFixtures, fetchFixtureTypes, fetchStamps]);
        },

        checkOnlineStatus({ commit, dispatch, state }) {
            console.warn("Checking status");
            if (navigator.onLine && state.online_status !== navigator.onLine) {
                dispatch('handleOnlineStatusChange');
            }
            commit('setOnlineStatus', navigator.onLine);
        },
    },

    getters: {
        getHeight: (state) => {
            return state.height;
        },

        getOffline: (state) => {
            return state.online_status;
        },

        getFCMToken: (state) => {
            return state.fcmToken;
        },

        getSyncing: (state) => {
            return state.syncing;
        },

        getLastSync: (state) => {
            return state.lastSync;
        },

        getActiveMenu: (state) => {
            return state.activeMenu;
        }
    }
});

export default store;